import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PageLoader from "./components/PageLoader";
import AediaXChatBot from "./components/AediaXChatBot";

// Lazy Pages
const Home = lazy(() => import("./pages/Home"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Content = lazy(() => import("./pages/Content"));
const ContentDetail = lazy(() => import("./pages/ContentDetail"));  
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Resources = lazy(() => import("./pages/Resources"));
const About = lazy(() => import("./pages/About"));

// Platform Pages
const AIEngineering = lazy(() => import("./pages/platform/AIEngineering"));
const CloudNative = lazy(() => import("./pages/platform/CloudNative"));
const EdgeComputing = lazy(() => import("./pages/platform/EdgeComputing"));
const Cybersecurity = lazy(() => import("./pages/platform/Cybersecurity"));
const Web3 = lazy(() => import("./pages/platform/Web3"));

// Company Pages
const AboutUs = lazy(() => import("./pages/company/AboutUs"));
const Careers = lazy(() => import("./pages/company/Careers"));
const Press = lazy(() => import("./pages/company/Press"));
const Contact = lazy(() => import("./pages/company/Contact"));

// Legal Pages
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const Security = lazy(() => import("./pages/legal/Security"));
const Compliance = lazy(() => import("./pages/legal/Compliance"));

// ScrollToTop component to handle page scrolling on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top instantly on route change
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant" // Use "smooth" for animated scrolling, "instant" for immediate
    });
    
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop /> {/* This will trigger scroll on every route change */}
      <Navbar />

      <Suspense
        fallback={
          <Box sx={{ minHeight: "80vh" }}>
            <PageLoader />
          </Box>
        }
      >
        <Routes>
          {/* Main Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/content" element={<Content />} />
          <Route path="/content/:id" element={<ContentDetail />} />
          <Route path="/tutorials" element={<Tutorials />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/about" element={<About />} />

          {/* Platform Routes */}
          <Route path="/platform/ai-engineering" element={<AIEngineering />} />
          <Route path="/platform/cloud-native" element={<CloudNative />} />
          <Route path="/platform/edge-computing" element={<EdgeComputing />} />
          <Route path="/platform/cybersecurity" element={<Cybersecurity />} />
          <Route path="/platform/web3" element={<Web3 />} />

          {/* Company Routes */}
          <Route path="/company/about" element={<AboutUs />} />
          <Route path="/company/careers" element={<Careers />} />
          <Route path="/company/blog" element={<Blog />} />
          <Route path="/company/press" element={<Press />} />
          <Route path="/company/contact" element={<Contact />} />

          {/* Legal Routes */}
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
          <Route path="/legal/security" element={<Security />} />
          <Route path="/legal/compliance" element={<Compliance />} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
      
      <AediaXChatBot />
      <Footer />
    </Router>
  );
}

export default App;